import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Button,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import gql from 'graphql-tag';
import { Query, Mutation, Subscription } from 'react-apollo';
import { GlobalContext } from '../../global-context';
import moment from 'moment';
import { Link } from 'react-router-dom';
import BoothIdLink from '../reusable/BoothIdLink';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
  },
  titleSection: {
    flex: 1,
  },
  buttonSection: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    position: 'relative',
    display: 'inline-block',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: -8,
      left: 0,
      width: 40,
      height: 3,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
    },
  },
  statusChip: {
    margin: theme.spacing(0, 0.5, 0.5, 0),
  },
  inWarehouse: {
    backgroundColor: theme.palette.success.light,
  },
  outWithCustomer: {
    backgroundColor: theme.palette.info.light,
  },
  inTransit: {
    backgroundColor: theme.palette.warning.light,
  },
  inactive: {
    backgroundColor: theme.palette.text.disabled,
  },
  infoCard: {
    height: '100%',
  },
  infoValue: {
    fontWeight: 500,
  },
  orderListItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  componentTable: {
    minWidth: 650,
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
  },
  backButton: {
    marginRight: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  activeButton: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  inactiveButton: {
    backgroundColor: theme.palette.grey[500],
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.grey[700],
    },
  },
  refreshButton: {
    backgroundColor: theme.palette.primary.dark,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  equipmentInfo: {
    backgroundColor: '#f0f8ff', // light blue background
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #b3e0ff',
    borderRadius: theme.shape.borderRadius,
  },
  equipmentIcon: {
    fontSize: '1.2rem',
    marginRight: theme.spacing(1),
  },
  equipmentTitle: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  equipmentChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500,
  },
}));

const getBoothDetailQuery = gql`
  query getBoothDetail($boothId: bigint!) {
    booths_by_pk(id: $boothId) {
      id
      sequence
      key_number
      ipad_udid
      active
      battery_level
      build_date
      created
      updated
      orders(order_by: { event_date: desc }, where: { active: { _eq: true } }) {
        id
        status
        event_name
        event_date
        ship_date
        return_date
        outbound_tracking
        inbound_tracking
        outbound_shipment_activity {
          shipped
          delivered
          latest_status_type
          latest_status_description
          updatedat
        }
        inbound_shipment_activity {
          shipped
          delivered
          latest_status_type
          latest_status_description
          updatedat
        }
      }
    }

    # Get the latest order for this booth to determine what equipment is likely in the case
    latest_booth_order: orders(
      where: { booth_id: { _eq: $boothId }, status: { _in: ["Done"] }, active: { _eq: true } }
      order_by: { id: desc }
      limit: 1
    ) {
      id
      status
      event_name
      event_date
      backdrop_color
      print_count
      props
    }
  }
`;

const subscribeToBoothDetailUpdates = gql`
  subscription subscribeToBoothDetail($boothId: bigint!) {
    booths_by_pk(id: $boothId) {
      id
      sequence
      key_number
      ipad_udid
      active
      battery_level
      build_date
      created
      updated
      orders(order_by: { event_date: desc }, where: { active: { _eq: true } }) {
        id
        status
        event_name
        event_date
        ship_date
        return_date
        outbound_tracking
        inbound_tracking
        outbound_shipment_activity {
          shipped
          delivered
          latest_status_type
          latest_status_description
          updatedat
        }
        inbound_shipment_activity {
          shipped
          delivered
          latest_status_type
          latest_status_description
          updatedat
        }
      }
    }
  }
`;

const updateBoothStatusMutation = gql`
  mutation updateBoothStatus($id: bigint!, $active: Boolean!) {
    update_booths_by_pk(pk_columns: { id: $id }, _set: { active: $active }) {
      id
      active
    }
  }
`;

// Helper function for consistent booth ID formatting
const formatBoothId = id => {
  const idStr = String(id);

  if (idStr.length > 3) {
    return `${idStr.substring(0, 3)}-${idStr.substring(3)}`;
  }

  return idStr;
};

// Add getLatestBoothOrderQuery
const getLatestBoothOrderQuery = gql`
  query getLatestBoothOrder($boothId: bigint!) {
    orders(
      where: { booth_id: { _eq: $boothId }, status: { _in: ["Done"] }, active: { _eq: true } }
      order_by: { id: desc }
      limit: 1
    ) {
      id
      status
      event_name
      event_date
      backdrop_color
      print_count
      props
    }
  }
`;

export default function BoothDetail(props) {
  const classes = useStyles();
  const ctx = useContext(GlobalContext);
  const boothId = parseInt(props.match.params.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(null);

  const handleStatusChangeClick = status => {
    setNewStatus(status);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const getBoothStatus = booth => {
    const activeOrder = booth.orders.find(
      order => order.status !== 'Done' && order.status !== 'Cancelled' && order.status !== 'Canceled'
    );

    if (!activeOrder) {
      return 'InWarehouse';
    }

    if (
      activeOrder.status === 'Outbound' &&
      activeOrder.outbound_shipment_activity &&
      !activeOrder.outbound_shipment_activity.delivered
    ) {
      return 'InTransitToCustomer';
    }

    if (
      (activeOrder.status === 'Inbound' || activeOrder.status === 'Returned') &&
      activeOrder.inbound_shipment_activity &&
      activeOrder.inbound_shipment_activity.shipped &&
      !activeOrder.inbound_shipment_activity.delivered
    ) {
      return 'InTransitToWarehouse';
    }

    if (
      activeOrder.status === 'Returned' &&
      activeOrder.inbound_shipment_activity &&
      activeOrder.inbound_shipment_activity.delivered
    ) {
      return 'ReturnedNotInbounded';
    }

    if (activeOrder.status === 'Checkin') {
      return 'InWarehouse';
    }

    return 'OutWithCustomer';
  };

  const getStatusChipProps = status => {
    switch (status) {
      case 'InWarehouse':
        return {
          label: 'In Warehouse',
          className: classes.inWarehouse,
          icon: <Icon fontSize='small'>home</Icon>,
        };
      case 'InTransitToCustomer':
        return {
          label: 'Outbound Shipping',
          className: classes.inTransit,
          icon: <Icon fontSize='small'>local_shipping</Icon>,
        };
      case 'OutWithCustomer':
        return {
          label: 'With Customer',
          className: classes.outWithCustomer,
          icon: <Icon fontSize='small'>business</Icon>,
        };
      case 'InTransitToWarehouse':
        return {
          label: 'Inbound Shipping',
          className: classes.inTransit,
          icon: <Icon fontSize='small'>assignment_return</Icon>,
        };
      case 'ReturnedNotInbounded':
        return {
          label: 'Returned (Not Checked In)',
          className: classes.inTransit,
          icon: <Icon fontSize='small'>assignment_turned_in</Icon>,
        };
      default:
        return {
          label: 'Inactive',
          className: classes.inactive,
          icon: <Icon fontSize='small'>block</Icon>,
        };
    }
  };

  return (
    <div className={classes.root}>
      <Subscription subscription={subscribeToBoothDetailUpdates} variables={{ boothId }}>
        {result => {
          const { loading, error, data } = result;

          if (loading) {
            return (
              <CircularProgress
                style={{
                  display: 'block',
                  margin: 'auto',
                }}
              />
            );
          }

          if (error) {
            if (ctx.handleNotifications) {
              ctx.handleNotifications(true, 'error', 'Failed to retrieve booth details: ' + error.toString());
            }

            return (
              <Paper className={classes.paper}>
                <Typography color='error'>Error loading booth details: {error.message}</Typography>
                <Button variant='contained' color='primary' onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </Paper>
            );
          }

          const booth = data?.booths_by_pk;

          if (!booth) {
            return (
              <Paper className={classes.paper}>
                <Typography variant='h5'>Booth not found</Typography>
                <Button
                  variant='contained'
                  color='primary'
                  component={Link}
                  to='/booths'
                  className={classes.buttonContainer}
                >
                  Back to Booths
                </Button>
              </Paper>
            );
          }

          const boothStatus = getBoothStatus(booth);
          const statusChipProps = getStatusChipProps(boothStatus);

          const activeOrder = booth.orders.find(
            order => order.status !== 'Done' && order.status !== 'Cancelled' && order.status !== 'Canceled'
          );

          return (
            <>
              <div className={classes.header}>
                <div className={classes.titleSection}>
                  <Button
                    variant='outlined'
                    className={classes.backButton}
                    startIcon={<Icon>arrow_back</Icon>}
                    component={Link}
                    to='/booths'
                  >
                    Back to Booths
                  </Button>

                  <Box mt={3}>
                    <Box display='flex' alignItems='center'>
                      <BoothIdLink boothId={booth.id} chip={true} linkToDetail={false} />
                      <Chip
                        icon={statusChipProps.icon}
                        label={statusChipProps.label}
                        className={`${classes.statusChip} ${statusChipProps.className}`}
                        size='medium'
                        style={{ marginLeft: '12px' }}
                      />
                      {booth.active !== undefined && (
                        <Chip
                          icon={<Icon fontSize='small'>{booth.active ? 'check_circle' : 'cancel'}</Icon>}
                          label={booth.active ? 'Active' : 'Out of Service'}
                          color={booth.active ? 'primary' : 'default'}
                          size='medium'
                          style={{ marginLeft: '12px' }}
                        />
                      )}
                    </Box>

                    <Typography variant='subtitle1' className={classes.subtitle}>
                      Last Updated: {moment(booth.updated).format('MMMM Do YYYY, h:mm:ss a')}
                    </Typography>
                  </Box>
                </div>

                <div className={classes.buttonSection}>
                  {(boothStatus === 'ReturnedNotInbounded' || (activeOrder && activeOrder.status === 'Checkin')) && (
                    <Button
                      variant='contained'
                      style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        marginRight: '8px',
                      }}
                      startIcon={<Icon>inventory_2</Icon>}
                      component={Link}
                      to={`/booths/${boothId}/inbound`}
                    >
                      {activeOrder && activeOrder.status === 'Checkin' ? 'Continue Inbound' : 'Inbound Process'}
                    </Button>
                  )}

                  {activeOrder && activeOrder.status === 'Preparing' && (
                    <Button
                      variant='contained'
                      style={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        marginRight: '8px',
                      }}
                      startIcon={<Icon>play_arrow</Icon>}
                      component={Link}
                      to={`/booths/${boothId}/outbound`}
                    >
                      Resume Outbound
                    </Button>
                  )}

                  {boothStatus === 'InWarehouse' && booth.active && !activeOrder && (
                    <Button
                      variant='contained'
                      style={{
                        backgroundColor: '#2196f3',
                        color: 'white',
                        marginRight: '8px',
                      }}
                      startIcon={<Icon>local_shipping</Icon>}
                      component={Link}
                      to={`/booths/${boothId}/outbound`}
                    >
                      Outbound Process
                    </Button>
                  )}

                  <Mutation
                    mutation={updateBoothStatusMutation}
                    onCompleted={() => {
                      ctx.handleNotifications(
                        true,
                        'success',
                        `Booth ${formatBoothId(boothId)} has been ${booth.active ? 'deactivated' : 'activated'}.`
                      );
                    }}
                    onError={err => {
                      ctx.handleNotifications(true, 'error', `Failed to update booth status: ${err.message}`);
                    }}
                  >
                    {(updateBoothStatus, { loading: mutationLoading }) => (
                      <>
                        <Button
                          variant='contained'
                          className={booth.active ? classes.inactiveButton : classes.activeButton}
                          startIcon={<Icon>{booth.active ? 'cancel' : 'check_circle'}</Icon>}
                          onClick={() => handleStatusChangeClick(!booth.active)}
                          disabled={mutationLoading}
                        >
                          {booth.active ? 'Mark Out of Service' : 'Mark as Active'}
                        </Button>

                        <Dialog
                          open={dialogOpen}
                          onClose={handleDialogClose}
                          aria-labelledby='alert-dialog-title'
                          aria-describedby='alert-dialog-description'
                        >
                          <DialogTitle id='alert-dialog-title'>
                            {newStatus ? 'Activate Booth?' : 'Deactivate Booth?'}
                          </DialogTitle>
                          <DialogContent>
                            <DialogContentText id='alert-dialog-description'>
                              {newStatus
                                ? `Are you sure you want to mark Booth #${boothId} as active and available for use?`
                                : `Are you sure you want to mark Booth #${boothId} as out of service? This booth will not be assigned to new orders.`}
                            </DialogContentText>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={handleDialogClose} color='default'>
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                updateBoothStatus({
                                  variables: {
                                    id: boothId,
                                    active: newStatus,
                                  },
                                });
                                handleDialogClose();
                              }}
                              color='primary'
                              variant='contained'
                              autoFocus
                            >
                              Confirm
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </>
                    )}
                  </Mutation>

                  <Button
                    variant='contained'
                    className={classes.refreshButton}
                    startIcon={<Icon>refresh</Icon>}
                    onClick={() => window.location.reload()}
                  >
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Equipment info section */}
              <Query query={getLatestBoothOrderQuery} variables={{ boothId }} fetchPolicy='network-only'>
                {result => {
                  const { loading: equipLoading, error: equipError, data: equipData } = result;

                  if (equipLoading) return null;
                  if (equipError) return null;

                  const latestOrder = equipData?.orders && equipData.orders.length > 0 ? equipData.orders[0] : null;

                  if (!latestOrder) return null;

                  return (
                    <Paper className={`${classes.paper} ${classes.equipmentInfo}`}>
                      <Typography className={classes.equipmentTitle}>
                        <Icon className={classes.equipmentIcon}>info</Icon>
                        Likely Equipment in Booth Case
                      </Typography>
                      <Typography variant='body2' gutterBottom>
                        Based on the latest order (#{latestOrder.id}) this booth was used for:
                      </Typography>

                      <Grid container spacing={2} style={{ marginTop: '8px' }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant='body2' style={{ fontWeight: 500 }}>
                            <Icon className={classes.equipmentIcon}>palette</Icon>
                            Backdrop:
                            <Chip
                              size='small'
                              className={classes.equipmentChip}
                              label={latestOrder.backdrop_color || 'Default'}
                              color={
                                latestOrder.backdrop_color && latestOrder.backdrop_color !== 'Default'
                                  ? 'primary'
                                  : 'default'
                              }
                            />
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant='body2' style={{ fontWeight: 500 }}>
                            <Icon className={classes.equipmentIcon}>print</Icon>
                            Printer:
                            <Chip
                              size='small'
                              className={classes.equipmentChip}
                              label={latestOrder.print_count > 0 ? 'Yes' : 'No'}
                              color={latestOrder.print_count > 0 ? 'primary' : 'default'}
                            />
                          </Typography>
                        </Grid>

                        {latestOrder.props && (
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant='body2' style={{ fontWeight: 500 }}>
                              <Icon className={classes.equipmentIcon}>emoji_objects</Icon>
                              Props:
                              <Chip
                                size='small'
                                className={classes.equipmentChip}
                                label={latestOrder.props || 'Default'}
                                color='primary'
                              />
                            </Typography>
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant='body2' style={{ fontWeight: 500 }}>
                            <Icon className={classes.equipmentIcon}>event</Icon>
                            Last Event:
                            <span style={{ marginLeft: '4px' }}>{latestOrder.event_name || 'Unknown'}</span>
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant='caption'
                        color='textSecondary'
                        style={{ marginTop: '8px', display: 'block' }}
                      >
                        This information is based on the last order processed with this booth
                        {latestOrder.event_date
                          ? ` (event date: ${moment(latestOrder.event_date).format('MMM D, YYYY')})`
                          : ''}
                      </Typography>
                    </Paper>
                  );
                }}
              </Query>

              {activeOrder && (
                <>
                  <Typography variant='h5' className={classes.sectionTitle}>
                    Current Order
                  </Typography>
                  <Paper className={classes.paper}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant='body2' color='textSecondary'>
                          Order ID
                        </Typography>
                        <Typography variant='body1' className={classes.infoValue}>
                          <Link to={`/orders/${activeOrder.id}`}>#{activeOrder.id}</Link>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant='body2' color='textSecondary'>
                          Event Name
                        </Typography>
                        <Typography variant='body1' className={classes.infoValue}>
                          {activeOrder.event_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant='body2' color='textSecondary'>
                          Status
                        </Typography>
                        <Typography variant='body1' className={classes.infoValue}>
                          {activeOrder.status}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant='body2' color='textSecondary'>
                          Event Date
                        </Typography>
                        <Typography variant='body1' className={classes.infoValue}>
                          {activeOrder.event_date ? moment(activeOrder.event_date).format('MM/DD/YYYY') : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant='body2' color='textSecondary'>
                          Outbound Tracking
                        </Typography>
                        <Typography variant='body1' className={classes.infoValue}>
                          {activeOrder.outbound_tracking || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant='body2' color='textSecondary'>
                          Inbound Tracking
                        </Typography>
                        <Typography variant='body1' className={classes.infoValue}>
                          {activeOrder.inbound_tracking || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </>
              )}

              <Typography variant='h5' className={classes.sectionTitle}>
                Order History
              </Typography>
              <Paper className={classes.paper}>
                {booth.orders.length > 0 ? (
                  <List>
                    {booth.orders.map(order => (
                      <ListItem
                        key={order.id}
                        className={classes.orderListItem}
                        component={Link}
                        to={`/orders/${order.id}`}
                        button
                      >
                        <ListItemIcon>
                          {order.status === 'Done' ? <Icon>check_circle</Icon> : <Icon>event</Icon>}
                        </ListItemIcon>
                        <ListItemText
                          primary={`#${order.id} - ${order.event_name}`}
                          secondary={`Status: ${order.status} | Event Date: ${
                            order.event_date ? moment(order.event_date).format('MM/DD/YYYY') : 'N/A'
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant='body1' align='center'>
                    No order history found for this booth
                  </Typography>
                )}
              </Paper>
            </>
          );
        }}
      </Subscription>
    </div>
  );
}
